import type {
  MembershipCardChangeInput,
  MembershipCardChangeResult,
  MembershipCardChangeStatus,
} from '../domain/cardChange';
import {
  isMembershipsDisabled,
  MembershipsDisabledError,
  toUpstream,
} from '../domain/errors';
import { isMembershipShape, isRecord } from '../domain/guards';
import { MembershipManageError, manageErrorKind } from '../domain/manage';
import {
  type MembershipCardDetails,
  type MembershipPaymentAction,
  MembershipPurchaseError,
  type MembershipPurchaseInput,
  type MembershipPurchaseResult,
  type MembershipPurchaseStatus,
  purchaseErrorKind,
} from '../domain/purchase';
import type {
  Membership,
  MembershipCharge,
  MembershipChargesPage,
  MembershipPlan,
} from '../domain/types';
import { MEMBERSHIP_ENDPOINT } from './endpoints';
import type { MembershipCardChangeGateway } from './MembershipCardChangeGateway';
import type {
  MembershipChargesQuery,
  MembershipGateway,
} from './MembershipGateway';
import type { MembershipManageGateway } from './MembershipManageGateway';
import type { MembershipPurchaseGateway } from './MembershipPurchaseGateway';

/** The slice of `RequestAPI` the gateway needs; both methods resolve to the body. */
export interface MembershipHttpClient {
  get(url: string, data?: Record<string, unknown>): Promise<unknown>;
  /** POST that is never re-sent; a lost answer is reconciled, not repeated. */
  postOnce(url: string, body: Record<string, unknown>): Promise<unknown>;
}

/** HTTP status of a failed request; `undefined` for network and non-HTTP failures. */
export const httpStatusOf = (error: unknown): number | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }
  const { response } = error;
  if (!isRecord(response)) {
    return undefined;
  }
  return typeof response.status === 'number' ? response.status : undefined;
};

const errorBodyOf = (error: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return undefined;
  }
  const body = error.response.data;
  return isRecord(body) ? body : undefined;
};

/**
 * Machine code of a failed request — `code`, else `data.errorCode`, else
 * `error.code`; `undefined` when absent.
 */
export const errorCodeOf = (error: unknown): string | undefined => {
  const body = errorBodyOf(error);
  if (!body) {
    return undefined;
  }
  if (typeof body.code === 'string') {
    return body.code;
  }
  if (isRecord(body.data) && typeof body.data.errorCode === 'string') {
    return body.data.errorCode;
  }
  return isRecord(body.error) && typeof body.error.code === 'string'
    ? body.error.code
    : undefined;
};

/** `metadata` of a failed request — at the top, under `data` or under `error`; `undefined` when absent. */
export const errorMetadataOf = (error: unknown): unknown => {
  const body = errorBodyOf(error);
  if (!body) {
    return undefined;
  }
  if (body.metadata !== undefined) {
    return body.metadata;
  }
  if (isRecord(body.data) && body.data.metadata !== undefined) {
    return body.data.metadata;
  }
  return isRecord(body.error) ? body.error.metadata : undefined;
};

/** Rethrows a 404 `MEMBERSHIPS_DISABLED` as `MembershipsDisabledError`, anything else as is. */
const rethrowRead = (error: unknown): never => {
  if (isMembershipsDisabled(httpStatusOf(error), errorCodeOf(error))) {
    throw new MembershipsDisabledError();
  }
  throw error;
};

const unwrapSingle = <T>(body: unknown): T =>
  (isRecord(body) && !('id' in body) && isRecord(body.data)
    ? body.data
    : body) as T;

const unwrapList = <T>(body: unknown): T[] => {
  if (Array.isArray(body)) {
    return body as T[];
  }
  if (isRecord(body) && Array.isArray(body.data)) {
    return body.data as T[];
  }
  return [];
};

const PURCHASE_STATUSES: ReadonlySet<string> = new Set([
  'trialing',
  'active',
  'action_required',
  'pending',
]);

const toPurchaseResult = (body: unknown): MembershipPurchaseResult => {
  const payload =
    isRecord(body) && !('status' in body) && isRecord(body.data)
      ? body.data
      : body;
  if (
    !isRecord(payload) ||
    typeof payload.status !== 'string' ||
    !PURCHASE_STATUSES.has(payload.status)
  ) {
    throw new MembershipPurchaseError('unknown_outcome');
  }
  const action =
    isRecord(payload.action) && typeof payload.action.type === 'string'
      ? (payload.action as MembershipPaymentAction)
      : null;
  if (payload.status === 'action_required' && action === null) {
    throw new MembershipPurchaseError('unknown_outcome');
  }
  return {
    status: payload.status as MembershipPurchaseStatus,
    action,
    membership: isRecord(payload.membership)
      ? unwrapSingle<Membership>(payload.membership)
      : null,
  };
};

const toPurchaseError = (error: unknown): MembershipPurchaseError => {
  if (error instanceof MembershipPurchaseError) {
    return error;
  }
  const status = httpStatusOf(error);
  const code = errorCodeOf(error);
  return new MembershipPurchaseError(
    purchaseErrorKind(status, code),
    status,
    code,
    toUpstream(errorMetadataOf(error)),
  );
};

const toManageError = (error: unknown): MembershipManageError => {
  if (error instanceof MembershipManageError) {
    return error;
  }
  const status = httpStatusOf(error);
  const code = errorCodeOf(error);
  return new MembershipManageError(
    manageErrorKind(status, code),
    status,
    code,
    toUpstream(errorMetadataOf(error)),
  );
};

/** A `/me` object in a `/me/*` answer; anything else leaves the outcome unknown. */
const toMembershipAnswer = (body: unknown): Membership => {
  const payload = unwrapSingle<unknown>(body);
  if (!isMembershipShape(payload)) {
    throw new MembershipManageError('unknown_outcome');
  }
  return payload;
};

const CARD_CHANGE_STATUSES: ReadonlySet<string> = new Set([
  'updated',
  'action_required',
  'pending',
]);

const toCardChangeResult = (body: unknown): MembershipCardChangeResult => {
  const payload =
    isRecord(body) && !('status' in body) && isRecord(body.data)
      ? body.data
      : body;
  if (
    !isRecord(payload) ||
    typeof payload.status !== 'string' ||
    !CARD_CHANGE_STATUSES.has(payload.status)
  ) {
    throw new MembershipManageError('unknown_outcome');
  }
  const action =
    isRecord(payload.action) && typeof payload.action.type === 'string'
      ? (payload.action as MembershipPaymentAction)
      : null;
  const tokenId =
    typeof payload.tokenId === 'string' && payload.tokenId !== ''
      ? payload.tokenId
      : null;
  if (
    payload.status === 'action_required' &&
    (action === null || tokenId === null)
  ) {
    throw new MembershipManageError('unknown_outcome');
  }
  const membership = isRecord(payload.membership)
    ? unwrapSingle<unknown>(payload.membership)
    : null;
  return {
    status: payload.status as MembershipCardChangeStatus,
    tokenId,
    action,
    membership: isMembershipShape(membership) ? membership : null,
  };
};

/**
 * Membership gateways over the customer service. A 404 on `/me` means "no
 * membership", and a 404 `MEMBERSHIPS_DISABLED` on any read a
 * `MembershipsDisabledError`; purchase requests are posted once and fail with a
 * `MembershipPurchaseError`, member card and "Add card" sheet (`?modal=club-card`) requests with a
 * `MembershipManageError` — neither keeps anything of the request.
 */
export class HttpMembershipGateway
  implements
    MembershipGateway,
    MembershipPurchaseGateway,
    MembershipManageGateway,
    MembershipCardChangeGateway
{
  constructor(private readonly http: MembershipHttpClient) {}

  async getMe(): Promise<Membership | null> {
    try {
      return unwrapSingle<Membership>(
        await this.http.get(MEMBERSHIP_ENDPOINT.me),
      );
    } catch (error) {
      if (
        httpStatusOf(error) === 404 &&
        !isMembershipsDisabled(404, errorCodeOf(error))
      ) {
        return null;
      }
      return rethrowRead(error);
    }
  }

  async getPlans(zoneId: string): Promise<MembershipPlan[]> {
    const body = await this.http
      .get(MEMBERSHIP_ENDPOINT.plans, { zoneId })
      .catch(rethrowRead);
    return unwrapList<MembershipPlan>(body);
  }

  async getCharges({
    page,
    size,
  }: MembershipChargesQuery): Promise<MembershipChargesPage> {
    const body = await this.http
      .get(MEMBERSHIP_ENDPOINT.charges, {
        page: { current: page, size },
      })
      .catch(rethrowRead);
    return {
      data: unwrapList<MembershipCharge>(body),
      pagination:
        isRecord(body) && isRecord(body.pagination)
          ? (body.pagination as MembershipChargesPage['pagination'])
          : null,
    };
  }

  purchase(input: MembershipPurchaseInput): Promise<MembershipPurchaseResult> {
    return this.send(MEMBERSHIP_ENDPOINT.purchase, { ...input });
  }

  confirmCard(
    details: MembershipCardDetails,
  ): Promise<MembershipPurchaseResult> {
    return this.send(MEMBERSHIP_ENDPOINT.confirmCard, { details });
  }

  cancel(): Promise<Membership> {
    return this.sendManage(MEMBERSHIP_ENDPOINT.cancel, {}, toMembershipAnswer);
  }

  changePlan(planId: string): Promise<Membership> {
    return this.sendManage(
      MEMBERSHIP_ENDPOINT.changePlan,
      { planId },
      toMembershipAnswer,
    );
  }

  cancelPlanChange(): Promise<Membership> {
    return this.sendManage(
      MEMBERSHIP_ENDPOINT.cancelPlanChange,
      {},
      toMembershipAnswer,
    );
  }

  changeCard(
    input: MembershipCardChangeInput,
  ): Promise<MembershipCardChangeResult> {
    return this.sendManage(
      MEMBERSHIP_ENDPOINT.changeCard,
      { ...input },
      toCardChangeResult,
    );
  }

  confirmCardChange(
    tokenId: string,
    details: MembershipCardDetails,
  ): Promise<MembershipCardChangeResult> {
    return this.sendManage(
      MEMBERSHIP_ENDPOINT.confirmCardChange,
      { tokenId, details },
      toCardChangeResult,
    );
  }

  private async sendManage<T>(
    url: string,
    body: Record<string, unknown>,
    parse: (answer: unknown) => T,
  ): Promise<T> {
    let answer: unknown;
    try {
      answer = await this.http.postOnce(url, body);
    } catch (error) {
      throw toManageError(error);
    }
    return parse(answer);
  }

  private async send(
    url: string,
    body: Record<string, unknown>,
  ): Promise<MembershipPurchaseResult> {
    let answer: unknown;
    try {
      answer = await this.http.postOnce(url, body);
    } catch (error) {
      throw toPurchaseError(error);
    }
    return toPurchaseResult(answer);
  }
}
